import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import PropTypes from "prop-types";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ListAltIcon from "@mui/icons-material/ListAlt";

import { showToast } from "../../../store/actions";
import { ButtonUI, ConfirmPopup } from "../../Common";
import { convertTimezone, setPrevState } from "../../../util/common";
import KMKTable from "../../Common/Table/KMKTable";
import DataSourceClass from "../../../service/dataSource";
import ApplyWarehouseHistory from "./ApplyWarehouseHistory";

const useStyles = makeStyles()((theme) => ({
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "52px",
    },
  },
  TableCheckbox: {
    align: "left",
    "&.Mui-disabled": {
      pointerEvents: "auto",
      cursor: "not-allowed",
    },
  },
  coloreRed: {
    color: theme.palette.error.main,
  },
  [theme.breakpoints.down("817")]: {
    historyContainer: {
      marginTop: "12px",
    },
  },
}));

const ConfigHistory = (props) => {
  const { resetHistory, setResetHistory, WorkspaceName } = props;
  const { Workspace_id } = useParams();

  const workspace = useSelector((state) => state.workspace);
  const { accessRole } = workspace.workspace;

  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [configDelete, setConfigDelete] = useState({
    unappliedFlag: false,
    loading: false,
    allowedFromHistory: [],
    notAllowedFromHistory: [],
    selectedLength: 0,
    allowedFromSelected: [],
    notAllowedFromSelected: [],
  });
  const [openApplyHistory, setOpenApplyHistory] = useState(false);
  const [configId, setConfigId] = useState(0);

  const [tableHeight, setTableHeight] = useState(window.innerHeight - 250);
  const historyActionRef = useRef(null);

  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  const { classes } = useStyles();
  const navigate = useNavigate();

  const handleCancel = () => {
    setSelectedItems([]);
    setHistoryList([]);
    setShowConfirm(false);
  };

  //Rallback to specific config
  const rollback = async () => {
    const { Version_id } = selectedItems[0];

    const dataSource = new DataSourceClass();
    setRollbackLoading(true);
    const response = await dataSource.rollbackToConfig(Workspace_id, Version_id);

    setShowConfirm(false);
    setRollbackLoading(false);

    if (response.success) {
      dispatchRef.current(
        showToast({
          message: response.message,
          type: "success",
        })
      );
      setResetHistory(true);
      handleCancel();
    }
  };

  const handleCompare = () => {
    const Version_id = Math.min(selectedItems[0].Version_id, selectedItems[1].Version_id);
    const Compare_id = Math.max(selectedItems[0].Version_id, selectedItems[1].Version_id);
    const ConfigVersions = {
      configVersion: selectedItems.filter((version) => Version_id === version.Version_id)[0],
      compareVersion: selectedItems.filter((version) => Compare_id === version.Version_id)[0],
    };
    window.localStorage.setItem("ConfigVersions", JSON.stringify(ConfigVersions));
    navigate(`/my-workspaces/${Workspace_id}/config/compare`);
  };

  const handleSelectUnapplied = useCallback(
    (e) => {
      setPrevState(setConfigDelete, { unappliedFlag: e.target.checked });
      if (e.target.checked) {
        setSelectedItems(configDelete.allowedFromHistory);
      } else setSelectedItems([]);
    },
    [configDelete.allowedFromHistory]
  );

  const handleDeleteConfig = async () => {
    setPrevState(setConfigDelete, { loading: true });
    const dataSource = new DataSourceClass();
    const response = await dataSource.deleteConfigHistory(Workspace_id, { selectedItems });
    setPrevState(setConfigDelete, { loading: false });

    if (response.success) {
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );
      setOpenDeleteConfirm(false);
      setResetHistory(true);
      handleCancel();
    }
  };

  const splitArrIntoTwo = useCallback((arr) => {
    let [success, failed] = arr.reduce(
      (prev, current) => {
        prev[!current.Applied && !current.isFirstOrLast ? 0 : 1].push(current);
        return prev;
      },
      [[], []]
    );
    return [success, failed];
  }, []);

  //Get History List for Config Data to Snowflake
  const getConfigHistoryList = useCallback(async () => {
    setLoading(true);
    setHistoryList([]);
    const dataSource = new DataSourceClass();
    const result = await dataSource.getConfigHistoryList(Workspace_id);

    if (result.success) {
      const data = result.data.historyData;
      if (data.length) {
        data[0].isFirstOrLast = true;
      }

      setHistoryList(data);
    }

    let vs = window.localStorage.getItem("ConfigVersions_");
    vs = JSON.parse(vs);
    if (vs && Object.keys(vs).length) setSelectedItems([vs.configVersion, vs.compareVersion]);
    window.localStorage.removeItem("ConfigVersions_");

    setLoading(false);
    setResetHistory(false);
  }, [Workspace_id, setResetHistory]);

  useEffect(() => {
    const handleResizeTable = () => setTableHeight(window.innerHeight - 190 - historyActionRef.current.clientHeight);

    handleResizeTable();
    window.addEventListener("resize", handleResizeTable);

    return () => window.removeEventListener("resize", handleResizeTable);
  }, [historyActionRef]);

  useEffect(() => {
    const [allowedFromHistory, notAllowedFromHistory] = splitArrIntoTwo(historyList);
    setPrevState(setConfigDelete, {
      allowedFromHistory: allowedFromHistory,
      notAllowedFromHistory: notAllowedFromHistory,
    });
  }, [historyList, splitArrIntoTwo]);

  useEffect(() => {
    const [allowedFromSelected, notAllowedFromSelected] = splitArrIntoTwo(selectedItems);
    let flag = configDelete.unappliedFlag;
    if (selectedItems.length === 0 && configDelete.unappliedFlag === true) flag = false;
    setPrevState(setConfigDelete, {
      selectedLength: selectedItems.length,
      allowedFromSelected: allowedFromSelected,
      notAllowedFromSelected: notAllowedFromSelected,
      unappliedFlag: flag,
    });
  }, [selectedItems, configDelete.unappliedFlag, splitArrIntoTwo]);

  useEffect(() => {
    if (resetHistory) getConfigHistoryList();
    return () => {};
  }, [getConfigHistoryList, resetHistory]);

  const closeHistory = () => {
    setOpenApplyHistory(false);
  };

  const handleRowSelect = useCallback((record) => {
    setSelectedItems((prev) => {
      if (prev.some(({ Version_id }) => Version_id === record.Version_id))
        return prev.filter(({ Version_id }) => Version_id !== record.Version_id);

      prev.push(record);
      return [...prev];
    });
  }, []);

  const HistoryColumns = useMemo(
    () => [
      {
        id: "SELECT_ALL",
        name: "",
        style: { maxWidth: 140, wordBreak: "break-word" },
        width: 140,
        align: "left",
        sortable: false,
        renderHeadCell: (name) => (
          <Tooltip
            title={<Typography variant="body2">Click to select all unapplied configs</Typography>}
            arrow
            placement="right"
            style={{ fontSize: "14px" }}
          >
            <Checkbox
              checked={
                configDelete.unappliedFlag ||
                (configDelete.selectedLength !== 0 &&
                  configDelete.allowedFromHistory.length === configDelete.selectedLength &&
                  configDelete.notAllowedFromSelected.length === 0)
              }
              indeterminate={
                configDelete.selectedLength !== 0 &&
                configDelete.allowedFromHistory.length !== configDelete.allowedFromSelected.length &&
                configDelete.notAllowedFromSelected.length === 0
              }
              disabled={!configDelete.allowedFromHistory.length}
              size="medium"
              color="info"
              className={classes.TableCheckbox}
              onChange={(e) => handleSelectUnapplied(e)}
              sx={{ marginLeft: "16px" }}
            />
          </Tooltip>
        ),
        renderValueCell: (val, record, index) => (
          <Checkbox
            value={record.Version_id}
            checked={selectedItems.some((v) => v.Version_id === record.Version_id)}
            disabled={configDelete.unappliedFlag && (record.Applied || record.isFirstOrLast)}
            size="medium"
            color="info"
            className={classes.TableCheckbox}
            onChange={() => handleRowSelect(record)}
            sx={{ marginLeft: "16px" }}
          />
        ),
      },
      {
        id: "CONFIG_ID",
        name: "CONFIG ID",
        style: { maxWidth: 130, wordBreak: "break-word" },
        width: 130,
        align: "left",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) =>
          record.Version_id === 0 ? `#${record.Version_id} (Current)` : `#${record.Version_id}`,
      },
      {
        id: "SAVED",
        name: "SAVED",
        style: { maxWidth: 290, wordBreak: "break-word" },
        width: 200,
        align: "left",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (record?.CreatedOn ? convertTimezone(record?.CreatedOn) : ""),
      },
      {
        id: "CHANGED_BY",
        name: "CHANGED BY",
        style: { maxWidth: 290, wordBreak: "break-word" },
        width: 200,
        align: "left",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => record.CreatedBy,
      },
      {
        id: "APPLIED",
        name: "APPLIED",
        style: { maxWidth: 180, wordBreak: "break-word" },
        width: 180,
        align: "center",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          if (!record.Applied)
            return record.HasRunQuery === 1 ? <span className={classes.coloreRed}>Apply Failed</span> : <span>-</span>;
          return <span>{record.Applied ? "Yes" : "No"}</span>;
        },
      },
      {
        id: "DETAILS",
        name: "DETAILS",
        style: { maxWidth: 120, wordBreak: "break-word" },
        width: 120,
        align: "center",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) =>
          record.HasRunQuery === 1 && (
            <Tooltip placement="top" title="View Details" arrow>
              <IconButton
                color={!record.Applied && record.HasRunQuery ? "error" : "inherit"}
                onClick={() => {
                  setOpenApplyHistory(true);
                  setConfigId(record.Version_id);
                }}
              >
                <ListAltIcon />
              </IconButton>
            </Tooltip>
          ),
      },
    ],
    [
      classes.TableCheckbox,
      classes.coloreRed,
      configDelete.allowedFromHistory.length,
      configDelete.allowedFromSelected.length,
      configDelete.notAllowedFromSelected.length,
      configDelete.selectedLength,
      configDelete.unappliedFlag,
      handleRowSelect,
      handleSelectUnapplied,
      selectedItems,
    ]
  );

  return (
    <>
      <Paper
        sx={{ width: "100%", overflow: "hidden" }}
        className={`${classes.DataTableContainer} ${classes.historyContainer}`}
      >
        <KMKTable
          loading={loading}
          columns={HistoryColumns}
          containerHeight={tableHeight}
          sortingProperties={{}}
          paginationProperties={{}}
          noDataText="No data available"
          data={historyList}
          boldHeader
          tableProps={{ size: "medium", stickyHeader: true }}
          dataIdKey="Workspace_id"
        />
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", mt: 2 }} ref={historyActionRef}>
        <Box alignItems="left">
          {[1, 2].includes(accessRole) && (
            <ButtonUI
              variant="contained"
              color="error"
              size="medium"
              disabled={configDelete.selectedLength === 0 || configDelete.notAllowedFromSelected.length !== 0}
              onClick={() => setOpenDeleteConfirm(true)}
              style={{ width: 100 }}
            >
              Delete
            </ButtonUI>
          )}
        </Box>
        <Box sx={{ marginLeft: "auto" }}>
          <Tooltip
            placement="top"
            arrow
            title={
              selectedItems.length <= 1 || selectedItems.length > 2 ? (
                <Typography variant="body2">Select any 2 Configuration to compare</Typography>
              ) : (
                ""
              )
            }
          >
            <span>
              <ButtonUI
                variant="contained"
                color="secondary"
                size="medium"
                disabled={selectedItems.length <= 1 || selectedItems.length > 2}
                onClick={() => handleCompare()}
                style={{ width: 100 }}
              >
                Compare
              </ButtonUI>
            </span>
          </Tooltip>
          {[1, 2].includes(accessRole) && (
            <ButtonUI
              variant="contained"
              color="error"
              size="medium"
              disabled={selectedItems.length !== 1 || selectedItems[0].Version_id === 0}
              onClick={() => setShowConfirm(true)}
              style={{ marginLeft: "15px", width: 100 }}
            >
              Rollback
            </ButtonUI>
          )}
        </Box>
      </Box>

      {showConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Config Rollback",
            popupMessage: `Are you sure you want to rollback to Config “#${selectedItems[0].Version_id}” of your data model?`,
            handleCancel: () => setShowConfirm(false),
            handleClick: rollback,
            loadingForConfirm: rollbackLoading,
          }}
        />
      )}

      {openDeleteConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Delete Config(s)",
            popupMessage: (
              <Box>
                <Typography
                  sx={{ marginBottom: "5px" }}
                >{`${selectedItems.length} record(s) will be deleted.`}</Typography>
                <Typography>Are you sure you want to proceed?</Typography>
              </Box>
            ),
            handleCancel: () => setOpenDeleteConfirm(false),
            handleClick: handleDeleteConfig,
            loadingForConfirm: configDelete.loading,
          }}
        />
      )}

      {openApplyHistory && (
        <ApplyWarehouseHistory handleClose={closeHistory} configId={configId} WorkspaceName={WorkspaceName} />
      )}
    </>
  );
};

ConfigHistory.propTypes = {
  resetHistory: PropTypes.bool.isRequired,
  setResetHistory: PropTypes.func.isRequired,
  WorkspaceName: PropTypes.string.isRequired,
};

export default React.memo(ConfigHistory);
